import React, { useEffect, useRef, useState } from 'react'
import { Box, Grid, Stack, Typography, Card, IconButton, CircularProgress, Tooltip } from '@mui/material';
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AgGridReact } from "ag-grid-react";
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
// icons
import VisibilityIcon from '@mui/icons-material/Visibility';
import AngleRightIcon from "common/customIcons/angle_right";
// utils and helpers
import { grey } from "@mui/material/colors";
import { checkSafeInventory, getDistributionConfig, getKitReport, holdDriverKit, verifyKitReport } from "../../../../redux/slices/hyperwolf/inventoryDistribution";
// custom components
import CommonLayout from "layouts/DashboardLayout";
import MDButton from "components/MDButton";
import { showToastMsg } from "components/distributionSetting/distributionManagement/addRegionKit";
import NoDataFound from "common/component/NoDataFound";
import { defaultColDef } from "components/tables/CommonAgGridTable";
import { UploadPhoto } from "common/uploadPhoto";
import ViewDiscrepancy from "common/CommonDrawer/hyperwolf/viewDiscrepancy";
import { Transition } from "components/distributionSetting/distributionManagement/distributionDetails";
import { TableLoader } from "components/tables/AgGridAccordionChildScrollingTable";
import { useSelector } from 'react-redux';

const KitReport = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const gridRef = useRef();
  const skipHoldOnUnmountRef = useRef(false);
  const { distributionId, driverId } = useParams()
  const [searchParams] = useSearchParams()
  const { regionId, subRegionId } = Object.fromEntries(searchParams.entries())

  const [details, setDetails] = useState({});
  const [isVerificationLoading, setIsVerificationLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(null);
  const [showDiscrepancyDetails, setShowDiscrepancyDetails] = useState(null);
  const [distributionConfigs, setDistributionConfigs] = useState({});
  const [canVerifyKit, setCanVerifyKit] = useState(false);
  const { user = {} } = useSelector(state => state.auth);

  const columnDefs = [
    {
      headerName: 'Product Name/SKU',
      field: 'name',
      suppressMenu: false,
      flex: 2,
      minWidth: 300,
      sortable: false,
      cellRenderer: (params) => {
        if (!params?.data) return "-";
        const { productName, sku, assets } = params?.data || {}
        const { thumbURL, name } = assets || {}

        return <Box style={{ display: "flex", gap: "10px" }}>
          {thumbURL && <img src={thumbURL} alt={name} width={30} height={30} />}
          <Box>
            <Typography variant='body2' style={{ color: "#2E2E3A", fontWeight: "500", fontSize: "12px", padding: "0" }}>{productName}</Typography>
            {sku && <Typography variant='body2' style={{ color: "#9A9AAF", fontSize: "12px", padding: "0" }}>{sku}</Typography>}
          </Box>
        </Box>
      }
    },
    {
      headerName: 'Brand',
      field: 'brandName',
      suppressMenu: false,
      flex: 1.25,
      minWidth: 160,
      sortable: false,
      valueGetter: ({ data }) => data?.brand?.name || "-"
    },
    // {
    //   headerName: 'Quantity',
    //   field: 'discrepancyQty',
    //   suppressMenu: false,
    //   flex: 1.25,
    //   minWidth: 140,
    //   sortable: false,
    // },
    {
      headerName: 'Batch',
      field: 'discrepancyBatch',
      suppressMenu: false,
      flex: 1.25,
      minWidth: 140,
      sortable: false,
      cellRenderer: ({ data }) => {
        const batches = data?.discrepancyBatches || [];
        const firstFive = batches.slice(0, 2);
        const rest = batches.slice(2);

        return (
          <Box>
            {firstFive.map((batch, idx) => (
              <Typography
                key={batch?.batchNo + idx}
                variant='body2'
                style={{ color: "#2E2E3A", fontWeight: "500", fontSize: "12px", padding: "0", lineHeight: "normal" }}
              >
                {batch?.sku} (Qty {batch?.qty})
              </Typography>
            ))}
            {rest.length > 0 && (
              <Tooltip
                title={
                  <Box>
                    {rest.map((batch, idx) => (
                      <Typography
                        key={batch?.batchNo + idx}
                        variant='body2'
                        style={{ color: "#ffffff", fontWeight: "500", fontSize: "12px", padding: "0", lineHeight: "normal" }}
                      >
                        {batch?.sku} (Qty {batch?.qty})
                      </Typography>
                    ))}
                  </Box>
                }
                placement="top"
                arrow
              >
                <Typography
                  variant='body2'
                  style={{
                    color: "#2E2E3A",
                    fontWeight: "500",
                    fontSize: "12px",
                    padding: "0",
                    cursor: "pointer",
                    textDecoration: "underline",
                    lineHeight: "normal"
                  }}
                >
                  +{rest.length} more
                </Typography>
              </Tooltip>
            )}
          </Box>
        );
      }
    },
    {
      headerName: 'Discrepancy Type',
      field: 'discrepancyType',
      suppressMenu: false,
      flex: 1.5,
      minWidth: 200,
      sortable: false,
      cellRenderer: ({ data = {} }) => {
        return <Box display="flex" alignItems="center" gap={0.5} className="text-discrepancy">
          <svg xmlns="http://www.w3.org/2000/svg" width="19" height="18" viewBox="0 0 19 18" fill="none">
            <path d="M7.88912 3.12793C9.04623 2.89781 10.2454 3.0163 11.3354 3.46777C12.4255 3.91928 13.3576 4.68403 14.0131 5.66504C14.6684 6.64587 15.018 7.79896 15.018 8.97852C15.0162 10.5601 14.3874 12.077 13.269 13.1953C12.1507 14.3136 10.6337 14.9425 9.0522 14.9443C7.87252 14.9442 6.71961 14.5939 5.73873 13.9385C4.75774 13.283 3.99298 12.3517 3.54146 11.2617C3.09001 10.1718 2.97157 8.97247 3.20162 7.81543C3.43179 6.65829 4.00021 5.59501 4.83443 4.76074C5.66871 3.92646 6.73193 3.35811 7.88912 3.12793ZM9.0522 11.1484C8.89156 11.1485 8.73463 11.1959 8.60103 11.2852C8.46726 11.3745 8.3628 11.5017 8.30123 11.6504C8.2397 11.799 8.22395 11.9624 8.25533 12.1201C8.28671 12.2779 8.36327 12.4233 8.47701 12.5371C8.59078 12.6509 8.7362 12.7284 8.894 12.7598C9.05171 12.7911 9.21516 12.7744 9.36373 12.7129C9.51237 12.6513 9.63958 12.5469 9.72896 12.4131C9.81818 12.2795 9.8656 12.1226 9.86568 11.9619C9.86568 11.7464 9.78065 11.5392 9.62838 11.3867C9.47582 11.2342 9.26795 11.1484 9.0522 11.1484ZM9.0522 5.18262C8.90855 5.18275 8.77098 5.24021 8.66939 5.3418C8.56782 5.44342 8.51029 5.58093 8.51021 5.72461V9.52148C8.51031 9.66515 8.56781 9.80269 8.66939 9.9043C8.77098 10.0059 8.90855 10.0633 9.0522 10.0635C9.19604 10.0635 9.33429 10.006 9.43599 9.9043C9.53757 9.80269 9.59507 9.66515 9.59517 9.52148V5.72461C9.59509 5.58093 9.53757 5.44342 9.43599 5.3418C9.33429 5.24009 9.19604 5.18262 9.0522 5.18262ZM9.40181 1.9375C11.1443 2.02383 12.798 2.75377 14.0376 3.99316C15.1511 5.10676 15.8514 6.55532 16.0454 8.10156C15.8501 6.55608 15.1487 5.10926 14.0356 3.99609C12.7964 2.7569 11.1438 2.02556 9.40181 1.9375Z" fill="#FD4438" />
          </svg>
          <Typography color="#FD4438" fontWeight="600" style={{ fontSize: "12px" }}>{data?.discrepancyType}</Typography>
        </Box>
      },
    },
    {
      headerName: '',
      field: 'action',
      suppressMenu: false,
      flex: 1.5,
      minWidth: 200,
      sortable: false,
      cellStyle: { textAlign: 'right', justifyContent: 'end' },
      headerClass: 'right-aligned-cell-header',
      cellRenderer: ({ data = {} }) => {
        return <MDButton
          onClick={() => {
            setShowDiscrepancyDetails(data);
          }}
          size='small'
          startIcon={<VisibilityIcon fontSize='small' width='14' height='14' />} variant='contained' fontWeight="500" color='primary' className='btn-primary-light'
          style={{ fontSize: "12px", minHeight: "auto" }}
        >
          View Discrepancy
        </MDButton>
      },
    },
  ];

  const handleGetConfigs = async () => {
    try {
      const response = await dispatch(getDistributionConfig()).unwrap()
      const data = response?.data;
      if (data) {
        setDistributionConfigs(data || {});
      }
    } catch (error) {
      console.log("error loading default value", error)
    }
  }

  const handleKitReport = async (filters = {}) => {
    try {
      if (gridRef.current) {
        gridRef.current?.api?.showLoadingOverlay();
      }

      const response = await dispatch(getKitReport({ distributionId, driverId, ...filters, limit: 100 })).unwrap();
      const { data } = response || {};

      if (!data?.discrepancyList?.length) {
        gridRef.current?.api?.showNoRowsOverlay();
      } else {
        gridRef.current?.api?.hideOverlay();
      }
      if (data) {
        setDetails(data || {});
        setCanVerifyKit(data.mode !== "Distribution")
      }
    }
    catch (error) {
      showToastMsg(error, "error");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setDetails(prev => ({ ...prev, [name]: value }));
  }

  const handleDeletePhoto = (image) => {
    setDetails(prev => ({ ...prev, photos: prev.photos.filter((img) => img !== image) }))
  }

  const handleVerify = async () => {
    if (!canVerifyKit || isVerificationLoading) return;

    try {
      setIsVerificationLoading(true);
      skipHoldOnUnmountRef.current = true;
      const response = await dispatch(verifyKitReport({ distributionId, regionId, subRegionId, driverId })).unwrap();
      if (response?.success) {
        showToastMsg(response?.message || "Kit report verified successfully", "success", undefined, { persist: true });
        navigate("/hyperwolf/driver-kit-verification")
      }
    }
    catch (error) {
      showToastMsg(error, "error", undefined, { persist: true });
    } finally {
      setIsVerificationLoading(false);
    }
  }

  const handleCheckSafeInventory = async () => {
    try {
      setIsLoading(true);

      const response = await dispatch(checkSafeInventory({ distributionId, subRegionId ,updatedBy:user?.name})).unwrap();
      showToastMsg(response?.message, "success", undefined, { persist: true });
      sessionStorage.setItem("canVerifyKit", JSON.stringify({ distributionId, subRegionId, canVerify: true }));
      setCanVerifyKit(true)
    } catch (error) {
      showToastMsg(error, "error", undefined, { persist: true });
      sessionStorage.removeItem("canVerifyKit");
    } finally {
      setIsLoading(false);
    }
  }

  const handleUpdateStatus = async (status) => {
    try {
      const response = await dispatch(holdDriverKit({ distributionId, driverId, regionId, subRegionId, status })).unwrap();

      if (response?.success) {
        showToastMsg(response?.message || "Success", "success")
      }
    }
    catch (error) {
      showToastMsg(error, "error")
    }
  }

  useEffect(() => {
    if (distributionId && driverId) {
      handleKitReport();
      // handleGetConfigs();
    }
  }, [distributionId, driverId]);

  useEffect(() => {
    const canVerifyKitValue = sessionStorage.getItem("canVerifyKit")
    if (canVerifyKitValue) {
      setCanVerifyKit(() => {
        const values = JSON.parse(canVerifyKitValue)
        if (values.distributionId === distributionId && values.subRegionId === subRegionId) {
          return values.canVerify
        }
        return false
      })
    }

    const handlePopstate = () => {
      skipHoldOnUnmountRef.current = true;
    };

    window.addEventListener('popstate', handlePopstate);

    return () => {
      window.removeEventListener('popstate', handlePopstate);
      sessionStorage.removeItem("canVerifyKit")

      if (!skipHoldOnUnmountRef.current) {
        handleUpdateStatus("hold");
      }
    };
  }, []);

  return (
    <>
      <CommonLayout>
        <Box p={4} className="cus-contents">
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
            gap={2}
            mb={2}
          >
            <Box display="flex" alignItems="center" color="text.main" lineHeight={1}>
              <IconButton onClick={() => {
                skipHoldOnUnmountRef.current = true;
                navigate(-1)
              }}>
                <AngleRightIcon fontSize="14px" />
              </IconButton>
              <Typography variant="h6" fontWeight="semibold" ml={0.75}>
                Products List
              </Typography>
            </Box>
          </Stack>

          {/* Top section */}
          <Card sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 2.5, boxShadow: "none" }}>
            <Grid container alignItems="center" rowSpacing={2} columnSpacing={2}>
              {/* Top details */}
              <Grid item xs={12} sm={4}>
                <Typography variant='h4' fontWeight="semibold">Kit Report</Typography>
              </Grid>

              {/* Action buttons */}
              <Grid item xs={12} sm={8}>
                <Stack direction='row' gap={1} alignItems='center' justifyContent="end">
                  <div className="d-flex align-item-top gap-2">
                    {details?.mode === "Distribution" && <MDButton
                      variant="contained"
                      color="primary"
                      sx={{ minWidth: "160px", background: "#2E2E3A !important", color: "#ffffff !important", boxShadow: "none !important" }}
                      disabled={isLoading}
                      onClick={handleCheckSafeInventory}
                    >
                      {isLoading ? <CircularProgress size={22} color="light" /> : "Safe Inventory"}
                    </MDButton>}
                    <MDButton
                      sx={{ minWidth: "160px" }}
                      variant="contained"
                      color="primary"
                      disabled={isVerificationLoading || !canVerifyKit}
                      onClick={handleVerify}
                    >
                      {isVerificationLoading ? <CircularProgress size={22} color="light" /> : "Verify"}
                    </MDButton>
                  </div>
                </Stack>
              </Grid>
            </Grid>
          </Card>

          <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 2.5, boxShadow: "none" }}>
            {/* Quantity data */}
            <Stack direction='row' gap={3} mb={3} alignItems='start' justifyContent="space-between">
              <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 2.5, boxShadow: "none", height: "100%", border: "1px solid #F2F3F7", flex: "1" }}>
                <div className="d-flex align-item-center gap-2 mb-3">
                  <div style={{ width: "30px", height: "30px", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "8px", background: "#F4792D", boxShadow: "0 12.522px 10.017px 0 rgba(255, 119, 22, 0.05), 0 6.65px 5.32px 0 rgba(255, 119, 22, 0.04), 0 2.767px 2.214px 0 rgba(255, 119, 22, 0.03)" }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <g clipPath="url(#clip0_17533_120975)">
                        <path d="M9 0.5621C4.34343 0.5621 0.5625 4.34302 0.5625 8.9996C0.5625 13.6562 4.34343 17.4371 9 17.4371C13.6566 17.4371 17.4375 13.6562 17.4375 8.9996C17.4375 7.70535 17.1446 6.4789 16.6229 5.38232L15.7852 6.22005C15.627 6.37912 15.4113 6.46882 15.187 6.46886H14.954C15.2856 7.24654 15.4693 8.1017 15.4693 8.9996C15.4693 12.5689 12.5693 15.4689 9 15.4689C5.43074 15.4689 2.5307 12.5689 2.5307 8.9996C2.5307 5.43034 5.43074 2.53026 9 2.53026C9.89807 2.53026 10.7535 2.71388 11.5313 3.04554V2.81262C11.5313 2.58829 11.621 2.37261 11.7801 2.21443L12.6172 1.37673C11.5207 0.854952 10.2942 0.5621 9 0.5621ZM14.3361 0.5621C14.2641 0.564246 14.1957 0.593893 14.1449 0.644984L12.1767 2.61318C12.1504 2.63931 12.1296 2.67041 12.1153 2.70463C12.1011 2.73885 12.0938 2.77553 12.0938 2.81259V4.66546L8.37982 8.37938C8.03937 8.71984 8.03937 9.28092 8.37982 9.62138C8.72028 9.96183 9.27973 9.96018 9.62018 9.61924L13.3336 5.90587H15.187C15.224 5.90586 15.2607 5.89856 15.2949 5.88433C15.3291 5.8701 15.3602 5.84926 15.3864 5.82298L17.3546 3.85475C17.3937 3.81535 17.4203 3.76526 17.431 3.71075C17.4418 3.65625 17.4361 3.59978 17.4148 3.54849C17.3934 3.4972 17.3574 3.45336 17.3112 3.42252C17.265 3.39169 17.2107 3.37522 17.1552 3.37519H15.8643L16.0884 3.1511C16.4288 2.81064 16.4305 2.25118 16.0901 1.91073C15.9199 1.7405 15.6942 1.65529 15.4688 1.65529C15.2434 1.65529 15.0183 1.74054 14.8481 1.91073L14.6245 2.1343V0.841765C14.6244 0.804315 14.6167 0.767266 14.6021 0.732799C14.5875 0.698332 14.5661 0.667153 14.5392 0.641059C14.5123 0.614964 14.4805 0.59449 14.4457 0.58085C14.4108 0.56721 14.3735 0.561153 14.3361 0.5621ZM9 4.49849C6.51805 4.49849 4.4989 6.51765 4.4989 8.9996C4.4989 11.4815 6.51805 13.4985 9 13.4985C11.4819 13.4985 13.4989 11.4815 13.4989 8.9996C13.4989 8.28354 13.3272 7.60828 13.0287 7.00665L11.4879 8.54748C11.5145 8.69443 11.5307 8.8451 11.5307 8.9996C11.5307 10.3942 10.3946 11.5303 9 11.5303C7.60536 11.5303 6.4693 10.3942 6.4693 8.9996C6.4693 7.60495 7.60536 6.46886 9 6.46886C9.15449 6.46886 9.30514 6.48503 9.45209 6.51172L10.9935 4.97033C10.3918 4.67123 9.71651 4.49849 9 4.49849Z" fill="white" />
                      </g>
                      <defs>
                        <clipPath id="clip0_17533_120975">
                          <rect width="18" height="18" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </div>
                  <Typography variant="h6" fontWeight="600" color="#2E2E3A">Expected Quantity</Typography>
                </div>
                <div className="d-grid grid-columns-three">
                  <div className="d-flex align-item-top flex-column gap-1">
                    <Typography variant='body2' color="#656575">Product Count</Typography>
                    <Typography variant='h6' color="#2E2E3A" fontWeight="600">
                      {details?.expectedQty?.productCount ? details?.expectedQty?.productCount?.toLocaleString() : "0"}
                    </Typography>
                  </div>
                  {/* {distributionConfigs.isRetailer && <div className="d-flex align-item-top flex-column gap-1">
                    <Typography variant='body2' color="#656575">Retail Value</Typography>
                    <Typography variant='h6' color="#2E2E3A" fontWeight="600">
                      ${details?.expectedQty?.retailValue ? details?.expectedQty?.retailValue?.toLocaleString() : "0"}
                    </Typography>
                  </div>} */}
                  <div className="d-flex align-item-top flex-column gap-1">
                    <Typography variant='body2' color="#656575">Kit Value</Typography>
                    <Typography variant='h6' color="#2E2E3A" fontWeight="600">
                      ${details?.expectedQty?.wholesaleValue ? details?.expectedQty?.wholesaleValue?.toLocaleString() : "0"}
                    </Typography>
                  </div>
                </div>
              </Card>
              <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 2.5, boxShadow: "none", height: "100%", border: "1px solid #F2F3F7", flex: "1" }}>
                <div className="d-flex align-item-center gap-2 mb-3">
                  <div style={{ width: "30px", height: "30px", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "8px", background: "#A162F7", boxShadow: "0 12.522px 10.017px 0 rgba(154, 22, 255, 0.05), 0 6.65px 5.32px 0 rgba(189, 22, 255, 0.04), 0 2.767px 2.214px 0 rgba(197, 22, 255, 0.03)" }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M16.3125 3.375V6.1875C16.3125 6.33668 16.2532 6.47976 16.1477 6.58525C16.0423 6.69074 15.8992 6.75 15.75 6.75C15.6008 6.75 15.4577 6.69074 15.3523 6.58525C15.2468 6.47976 15.1875 6.33668 15.1875 6.1875V3.9375H12.9375C12.7883 3.9375 12.6452 3.87824 12.5398 3.77275C12.4343 3.66726 12.375 3.52418 12.375 3.375C12.375 3.22582 12.4343 3.08274 12.5398 2.97725C12.6452 2.87176 12.7883 2.8125 12.9375 2.8125H15.75C15.8992 2.8125 16.0423 2.87176 16.1477 2.97725C16.2532 3.08274 16.3125 3.22582 16.3125 3.375ZM5.0625 14.0625H2.8125V11.8125C2.8125 11.6633 2.75324 11.5202 2.64775 11.4148C2.54226 11.3093 2.39918 11.25 2.25 11.25C2.10082 11.25 1.95774 11.3093 1.85225 11.4148C1.74676 11.5202 1.6875 11.6633 1.6875 11.8125V14.625C1.6875 14.7742 1.74676 14.9173 1.85225 15.0227C1.95774 15.1282 2.10082 15.1875 2.25 15.1875H5.0625C5.21168 15.1875 5.35476 15.1282 5.46025 15.0227C5.56574 14.9173 5.625 14.7742 5.625 14.625C5.625 14.4758 5.56574 14.3327 5.46025 14.2273C5.35476 14.1218 5.21168 14.0625 5.0625 14.0625ZM15.75 11.25C15.6008 11.25 15.4577 11.3093 15.3523 11.4148C15.2468 11.5202 15.1875 11.6633 15.1875 11.8125V14.0625H12.9375C12.7883 14.0625 12.6452 14.1218 12.5398 14.2273C12.4343 14.3327 12.375 14.4758 12.375 14.625C12.375 14.7742 12.4343 14.9173 12.5398 15.0227C12.6452 15.1282 12.7883 15.1875 12.9375 15.1875H15.75C15.8992 15.1875 16.0423 15.1282 16.1477 15.0227C16.2532 14.9173 16.3125 14.7742 16.3125 14.625V11.8125C16.3125 11.6633 16.2532 11.5202 16.1477 11.4148C16.0423 11.3093 15.8992 11.25 15.75 11.25ZM2.25 6.75C2.39918 6.75 2.54226 6.69074 2.64775 6.58525C2.75324 6.47976 2.8125 6.33668 2.8125 6.1875V3.9375H5.0625C5.21168 3.9375 5.35476 3.87824 5.46025 3.77275C5.56574 3.66726 5.625 3.52418 5.625 3.375C5.625 3.22582 5.56574 3.08274 5.46025 2.97725C5.35476 2.87176 5.21168 2.8125 5.0625 2.8125H2.25C2.10082 2.8125 1.95774 2.87176 1.85225 2.97725C1.74676 3.08274 1.6875 3.22582 1.6875 3.375V6.1875C1.6875 6.33668 1.74676 6.47976 1.85225 6.58525C1.95774 6.69074 2.10082 6.75 2.25 6.75ZM12.51 5.85773L9.28125 4.01133C9.19624 3.96274 9.10002 3.93718 9.00211 3.93718C8.90419 3.93718 8.80798 3.96274 8.72297 4.01133L5.49 5.85773C5.44604 5.88206 5.4094 5.91771 5.38388 5.96099C5.35837 6.00427 5.34491 6.05359 5.34491 6.10383C5.34491 6.15407 5.35837 6.20339 5.38388 6.24667C5.4094 6.28995 5.44604 6.3256 5.49 6.34992L9 8.35242L12.51 6.34641C12.5531 6.32183 12.589 6.28628 12.6139 6.24336C12.6388 6.20045 12.652 6.1517 12.652 6.10207C12.652 6.05244 12.6388 6.00369 12.6139 5.96078C12.589 5.91787 12.5531 5.88231 12.51 5.85773ZM4.5 7.5607V11.25C4.49911 11.3496 4.52468 11.4476 4.57409 11.5341C4.6235 11.6206 4.69499 11.6924 4.78125 11.7422L8.01562 13.5893C8.05841 13.614 8.10695 13.627 8.15635 13.627C8.20576 13.627 8.25429 13.6139 8.29705 13.5892C8.33982 13.5645 8.37532 13.5289 8.39998 13.4861C8.42463 13.4433 8.43757 13.3947 8.4375 13.3453V9.32625L4.92188 7.31672C4.87909 7.29202 4.83055 7.27902 4.78115 7.27904C4.73174 7.27906 4.68321 7.29209 4.64045 7.31682C4.59768 7.34156 4.56218 7.37712 4.53752 7.41993C4.51287 7.46275 4.49993 7.5113 4.5 7.5607ZM13.5 11.25V7.5607C13.5001 7.5113 13.4871 7.46275 13.4625 7.41993C13.4378 7.37712 13.4023 7.34156 13.3596 7.31682C13.3168 7.29209 13.2683 7.27906 13.2189 7.27904C13.1694 7.27902 13.1209 7.29202 13.0781 7.31672L9.5625 9.32625V13.3418C9.56243 13.3912 9.57537 13.4398 9.60003 13.4826C9.62468 13.5254 9.66018 13.5609 9.70294 13.5857C9.74571 13.6104 9.79424 13.6234 9.84365 13.6235C9.89305 13.6235 9.94159 13.6105 9.98438 13.5858L13.2188 11.7422C13.305 11.6924 13.3765 11.6206 13.4259 11.5341C13.4753 11.4476 13.5009 11.3496 13.5 11.25Z" fill="white" />
                    </svg>
                  </div>
                  <Typography variant="h6" fontWeight="600" color="#2E2E3A">Scanned Quantity</Typography>
                </div>
                <div className="d-grid grid-columns-three">
                  <div className="d-flex align-item-top flex-column gap-1">
                    <Typography variant='body2' color="#656575">Product Count</Typography>
                    <Typography variant='h6' color="#2E2E3A" fontWeight="600">{details?.scannedQty?.productCount || "0"}</Typography>
                  </div>
                  {/* {distributionConfigs.isRetailer && <div className="d-flex align-item-top flex-column gap-1">
                    <Typography variant='body2' color="#656575">Retail Value</Typography>
                    <Typography variant='h6' color="#2E2E3A" fontWeight="600">${details?.scannedQty?.retailValue || "0"}</Typography>
                  </div>} */}
                  <div className="d-flex align-item-top flex-column gap-1">
                    <Typography variant='body2' color="#656575">Kit Value</Typography>
                    <Typography variant='h6' color="#2E2E3A" fontWeight="600">${details?.scannedQty?.wholesaleValue || "0"}</Typography>
                  </div>
                </div>
              </Card>
            </Stack>

            <Grid container alignItems="start" spacing={2} mt={0}>
              {/* Additional notes */}
              <Grid item xs={12}>
                <Typography variant="h5" fontWeight="600" color="#2E2E3A">Additional Note</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Additional Note</Typography>
                <textarea
                  placeholder='Add Note...'
                  style={{
                    width: "100%", minHeight: "130px", borderRadius: "20px", boxShadow: 'none', border: "1px solid #9A9AAF", overflow: "hidden", outline: "none", padding: "12px 20px", resize: "none"
                  }}
                  name="notes"
                  value={details.notes || ""}
                  onChange={handleChange}
                />
              </Grid>

              {/* Upload file */}
              <Grid item xs={12} md={6}>
                <UploadPhoto
                  photos={details.photos}
                  handleDeletePhoto={handleDeletePhoto}
                  updatePhotos={photo => setDetails(prev => ({ ...prev, photos: [...(prev.photos || []), photo] }))}
                />
              </Grid>
            </Grid>
          </Card>

          {/*Discrepancy Listing */}
          <Grid container alignItems="center" rowSpacing={2} columnSpacing={2} mt={1} className="table-nodata-size">
            <Grid item xs={12}>
              <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Discrepancy List</Typography>
              <div
                className="ag-theme-quartz"
                style={{ height: "450px", width: "100%" }}
              >
                <AgGridReact
                  ref={gridRef}
                  className="ag-grid--table"
                  pagination={false}
                  rowData={details.discrepancyList?.length ? details.discrepancyList : null}
                  noRowsOverlayComponent={NoDataFound}
                  loadingOverlayComponent={TableLoader}
                  columnDefs={columnDefs}
                  rowModelType={"clientSide"}
                  headerHeight={36}
                  rowHeight={56}
                  rowSelection="multiple"
                  suppressRowClickSelection={true}
                  defaultColDef={{ ...defaultColDef }}
                />
              </div>
            </Grid>
          </Grid>
        </Box>
      </CommonLayout>

      {
        showDiscrepancyDetails &&
        <ViewDiscrepancy
          open={Boolean(showDiscrepancyDetails)}
          TransitionComponent={Transition}
          handleClose={() => setShowDiscrepancyDetails(null)}
          data={showDiscrepancyDetails}
        />
      }
    </>
  )
}

export default KitReport
